import type { PerformanceLog } from '@/entities/performance/domain/types';

const STORAGE_KEY_LEGACY = 'performance_logs';
const STORAGE_KEY_LEGACY_SUCCESS = 'performance_logs_success';
const STORAGE_KEY_ERROR = 'performance_logs_error';
const MAX_ERROR_LOGS = 100;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const isPerformanceIssue = (log: PerformanceLog): boolean => {
  if (log.type === 'slow_query' || log.type === 'timeout' || log.type === 'connection_reset_retry') {
    return true;
  }

  const status = log.data.status as number | string | undefined;
  if (!status) return false;

  const statusStr = String(status);
  return statusStr.startsWith('4') || statusStr.startsWith('5') || statusStr === 'error';
};

const migrateLegacyLogs = (): void => {
  try {
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (legacy) {
      const logs: PerformanceLog[] = JSON.parse(legacy);
      const performanceIssueLogs = logs.filter(isPerformanceIssue);
      if (performanceIssueLogs.length > 0) {
        performanceStorage.saveLogs(performanceIssueLogs);
      }
      localStorage.removeItem(STORAGE_KEY_LEGACY);
    }

    const legacySuccess = localStorage.getItem(STORAGE_KEY_LEGACY_SUCCESS);
    if (legacySuccess) {
      localStorage.removeItem(STORAGE_KEY_LEGACY_SUCCESS);
    }
  } catch (error) {
    console.error('Failed to migrate legacy logs:', error);
  }
};

export const performanceStorage = {
  getLogs: (): PerformanceLog[] => {
    try {
      migrateLegacyLogs();

      const stored = localStorage.getItem(STORAGE_KEY_ERROR);
      if (!stored) return [];

      const logs: PerformanceLog[] = JSON.parse(stored);
      const now = Date.now();

      const validLogs = logs.filter((log) => now - log.timestamp < MAX_AGE_MS);
      validLogs.sort((a, b) => b.timestamp - a.timestamp);

      return validLogs;
    } catch (error) {
      console.error('Failed to load performance logs:', error);
      return [];
    }
  },

  getErrorLogs: (): PerformanceLog[] => {
    return performanceStorage.getLogs();
  },

  saveLogs: (logs: PerformanceLog[]): void => {
    try {
      const performanceIssueLogs = logs.filter(isPerformanceIssue);
      const trimmedLogs = performanceIssueLogs.slice(-MAX_ERROR_LOGS);
      localStorage.setItem(STORAGE_KEY_ERROR, JSON.stringify(trimmedLogs));
    } catch (error) {
      console.error('Failed to save performance logs:', error);
    }
  },

  addLog: (log: PerformanceLog): void => {
    if (!isPerformanceIssue(log)) {
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY_ERROR);
      const logs: PerformanceLog[] = stored ? JSON.parse(stored) : [];

      logs.push(log);

      const now = Date.now();
      const validLogs = logs.filter((l) => now - l.timestamp < MAX_AGE_MS);
      const trimmedLogs = validLogs.slice(-MAX_ERROR_LOGS);

      localStorage.setItem(STORAGE_KEY_ERROR, JSON.stringify(trimmedLogs));
    } catch (error) {
      console.error('Failed to add performance log:', error);
    }
  },

  clearLogs: (): void => {
    localStorage.removeItem(STORAGE_KEY_ERROR);
    localStorage.removeItem(STORAGE_KEY_LEGACY_SUCCESS);
  },

  getLogById: (id: string): PerformanceLog | undefined => {
    const logs = performanceStorage.getLogs();
    return logs.find((log) => log.id === id);
  },
};
